import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { BankResponse, fetchBanksByCountry } from '../utils/banksApi';
import { getTranslatedCountries } from '../utils/countries';
import { Country } from '../types';

interface UseCountriesAndBanksReturn {
  countries: Country[];
  banks: BankResponse[];
  selectedCountry: string | null;
  selectedBank: string | null;
  loading: boolean;
  error: string | null;
  setSelectedCountry: (countryId: string | null) => void;
  setSelectedBank: (bankId: string | null) => void;
}

export const useCountriesAndBanks = (): UseCountriesAndBanksReturn => {
  const { t, i18n } = useTranslation();
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedBank, setSelectedBank] = useState<string | null>(null);

  // Load countries with translations
  useEffect(() => {
    try {
      setCountries(getTranslatedCountries(t));
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  }, [t, i18n.language]);

  // Fetch banks for selected country using React Query
  const {
    data: banks = [],
    isLoading,
    error: banksError
  } = useQuery({
    queryKey: ['banks', selectedCountry, i18n.language],
    queryFn: () => selectedCountry ? fetchBanksByCountry(selectedCountry, i18n.language) : Promise.resolve([]),
    enabled: !!selectedCountry,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Reset bank selection when country changes
  useEffect(() => {
    setSelectedBank(null);
  }, [selectedCountry]);

  const handleSetSelectedCountry = (countryId: string | null) => {
    setSelectedCountry(countryId);
  };

  const handleSetSelectedBank = (bankId: string | null) => {
    setSelectedBank(bankId);
  };

  return {
    countries,
    banks,
    selectedCountry,
    selectedBank,
    loading: isLoading,
    error: banksError?.message || null,
    setSelectedCountry: handleSetSelectedCountry,
    setSelectedBank: handleSetSelectedBank,
  };
}; 